import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import { HttpService } from 'core/services/http';
import { UserHttpService } from 'core/services/user';
import React, { useEffect, useMemo, useState } from 'react';
import { keyframes } from '@mui/system';
import { ThemeProvider, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { buildCmsTheme } from '../../../core/context/ThemeContext';
import { useForm } from 'react-hook-form';

const drift = keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(40px, -30px, 0) scale(1.12); }
  100% { transform: translate3d(0, 0, 0) scale(1); }
`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(139,156,247,.45), 0 10px 30px -8px rgba(139,156,247,.7); }
  50%      { box-shadow: 0 0 0 10px rgba(139,156,247,0), 0 10px 40px -6px rgba(139,156,247,.9); }
`;

function LogindPage() {
    // The login screen is always dark-glass, independent of the saved CMS mode.
    const theme = useMemo(() => buildCmsTheme('dark'), []);
    useEffect(() => {
        // Add class to body to force LTR for CMS login
        document.body.classList.add('cms-ltr');
        return () => {
            document.body.classList.remove('cms-ltr');
        };
    }, []);
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const getResponseError = (response) => {
        if (!response) return 'Unable to sign in. Please try again.';
        if (response.detail) return response.detail;
        if (response.email) return response.email;
        if (Array.isArray(response) && response[0]) return response[0];
        return 'Invalid credentials. Please try again.';
    };

    const OnSubmit = async (data) => {
        if (loading) return;

        setLoading(true);
        setErrorMessage("");
        const body = {
            "email": data.email,
            "username": data.email,
            "password": data.password
        };

        try {
            const response = await UserHttpService.login(body);
            if (response?.role) {
                HttpService.setToken(response.tokens.access);
                HttpService.setUser(response);

                if (response.role === 'admin') {
                    window.location.href = "/cms/dashboard";
                }
                else {
                    window.location.href = "/";
                }
                return;
            }
            setErrorMessage(getResponseError(response));
        } catch (error) {
            setErrorMessage('Unable to sign in. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const orb = (color, size, top, left, delay) => ({
        position: 'absolute',
        width: size,
        height: size,
        top,
        left,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
        filter: 'blur(40px)',
        opacity: 0.7,
        animation: `${drift} 14s ease-in-out ${delay}s infinite`,
        pointerEvents: 'none'
    });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    color: 'text.primary'
                }}
            >
                <Box sx={orb('rgba(99,112,230,.55)', 460, '-8%', '-6%', 0)} />
                <Box sx={orb('rgba(56,189,178,.35)', 420, '55%', '70%', 3)} />
                <Box sx={orb('rgba(168,85,247,.32)', 380, '62%', '-8%', 6)} />

                <Paper
                    elevation={0}
                    sx={(t) => ({
                        position: 'relative',
                        width: '100%',
                        maxWidth: 400,
                        p: { xs: 3, md: 4 },
                        borderRadius: '26px',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(26px) saturate(170%)',
                        WebkitBackdropFilter: 'blur(26px) saturate(170%)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: `0 30px 80px -30px ${alpha(t.palette.primary.main, 0.55)}, inset 0 1px 0 rgba(255,255,255,0.12)`,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: -1,
                            borderRadius: 'inherit',
                            padding: '1px',
                            background: `linear-gradient(140deg, ${alpha(t.palette.primary.light, 0.6)}, transparent 40%, ${alpha(t.palette.secondary.main, 0.35)})`,
                            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            pointerEvents: 'none'
                        }
                    })}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3.5 }}>
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '16px',
                                mb: 2,
                                color: '#0a0e1a',
                                background: 'linear-gradient(135deg, #b4bffb, #8b9cf7 55%, #6472d6)',
                                animation: `${pulse} 3.2s ease-in-out infinite`
                            }}
                        >
                            <LockOutlinedIcon />
                        </Box>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            Job Flow Connect
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Sign in to access the CMS dashboard
                        </Typography>
                    </Box>

                    {errorMessage && (
                        <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit(OnSubmit)} noValidate>
                        <TextField
                            fullWidth
                            label="Email address"
                            type="email"
                            margin="normal"
                            autoComplete="email"
                            {...register("email", { required: "Email is required" })}
                            error={Boolean(errors.email)}
                            helperText={errors.email?.message}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                )
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            margin="normal"
                            autoComplete="current-password"
                            {...register("password", { required: "Password is required" })}
                            error={Boolean(errors.password)}
                            helperText={errors.password?.message}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <FormControlLabel
                            control={<Checkbox size="small" color="primary" />}
                            label={<Typography variant="body2" color="text.secondary">Remember me</Typography>}
                            sx={{ mt: 0.5 }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={loading}
                            sx={{ mt: 2, py: 1.25, borderRadius: '12px', fontSize: '0.9rem' }}
                        >
                            {loading ? 'Signing in…' : 'Sign in'}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </ThemeProvider>
    );
}

export default LogindPage;
